import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Stack,
  alpha,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday,
  LocalHospital,
  Assignment,
  AttachMoney,
  Message,
  Phone,
  Email,
  LocationOn,
  AccessTime,
  ChevronRight,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Chat as ChatIcon,
  MedicalServices as MedicalServicesIcon,
  Verified as VerifiedIcon,
  NotVerified as NotVerifiedIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getPatientDetails } from '../../features/patients/patientSlice';
import { format } from 'date-fns';

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`patient-tabpanel-${index}`}
      aria-labelledby={`patient-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const PatientDetailsModal = ({ open, onClose, patientId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const { selectedPatient, patientAppointments, selectedLoading, selectedError } = useSelector(
    (state) => state.patients
  );
  
  // Use selectedLoading and selectedError for modal-specific loading/error states
  const loading = selectedLoading;
  const error = selectedError;

  useEffect(() => {
    if (open && patientId) {
      console.log('📋 Modal: Fetching patient details for ID:', patientId);
      dispatch(getPatientDetails(patientId))
        .unwrap()
        .then((data) => {
          console.log('✅ Modal: Patient details loaded:', data);
        })
        .catch((error) => {
          console.error('❌ Modal: Error loading patient details:', error);
        });
    } else if (open && !patientId) {
      console.warn('⚠️ Modal: Open but no patientId provided');
    }
  }, [dispatch, open, patientId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStartConsultation = () => {
    navigate(`/doctor-dashboard/consultation/${patientId}`);
    onClose();
  };

  const handleViewProfile = () => {
    navigate(`/doctor-dashboard/patients/${patientId}`);
    onClose();
  };

  const getAppointmentStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getAppointmentStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'cancelled':
        return <CancelIcon />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <Skeleton variant='text' width={200} />
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Skeleton variant='circular' width={100} height={100} />
            <Skeleton variant='text' width='60%' />
            <Skeleton variant='text' width='40%' />
            <Skeleton variant='rectangular' height={200} />
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <Typography variant='h6'>Error</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity='error'>{error}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle case when modal is open but patient data is not yet loaded
  if (open && !selectedPatient && !loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Typography variant='h6'>Patient Details</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity='warning'>
            Patient data not available. Please close and try again.
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // Don't render if not open or no patient data
  if (!open || !selectedPatient) {
    return null;
  }

  // Get patient data - selectedPatient is the patient object directly from Redux
  const patient = selectedPatient;
  const appointments = patientAppointments || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: isMobile ? '90vh' : '80vh',
        },
      }}
    >
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6'>Patient Details</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Patient Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Avatar
            sx={{
              width: 100,
              height: 100,
              margin: '0 auto',
              mb: 2,
              bgcolor: theme.palette.primary.main,
              fontSize: '2rem',
            }}
          >
            {patient?.name?.charAt(0) || '?'}
          </Avatar>
          <Typography variant='h5' gutterBottom>
            {patient?.name || 'N/A'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Patient ID: {patient?.id || 'N/A'}
          </Typography>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
              }}
            >
              <Typography variant='h6'>
                {appointments?.length || 0}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Appointments
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                borderRadius: 2,
              }}
            >
              <Typography variant='h6'>
                {appointments?.filter(a => a.status === 'Completed').length || 0}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Completed
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.success.main, 0.1),
                borderRadius: 2,
              }}
            >
              <Typography variant='h6'>{patient?.age || 'N/A'}</Typography>
              <Typography variant='body2' color='text.secondary'>
                Age
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 2,
              }}
            >
              <Typography variant='h6'>{patient?.gender || 'N/A'}</Typography>
              <Typography variant='body2' color='text.secondary'>
                Gender
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Contact Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant='h6' gutterBottom>
            Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone color='primary' />
                <Typography>{patient?.phone_number || 'N/A'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email color='primary' />
                <Typography>{patient?.email || 'N/A'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color='primary' />
                <Typography>{patient?.address || 'N/A'}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label='Recent Activity' />
            <Tab label='Appointments' />
            <Tab label='Medical History' />
            <Tab label='Documents' />
            <Tab label='Notes' />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <List>
            {appointments.slice(0, 5).map((appointment) => (
              <ListItem key={appointment.id}>
                <ListItemIcon>
                  {getAppointmentStatusIcon(appointment.status)}
                </ListItemIcon>
                <ListItemText
                  primary={`${appointment.type} - ${appointment.formatted_date} ${appointment.formatted_time}`}
                  secondary={`Status: ${appointment.status}`}
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <List>
            {appointments.map((appointment) => (
              <ListItem key={appointment.id}>
                <ListItemIcon>
                  {getAppointmentStatusIcon(appointment.status)}
                </ListItemIcon>
                <ListItemText
                  primary={`${appointment.type} - ${appointment.formatted_date} ${appointment.formatted_time}`}
                  secondary={
                    <Box>
                      <Typography variant='body2' color='text.secondary'>
                        Status: {appointment.status}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Duration: {appointment.duration} minutes
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Stack spacing={2}>
            {selectedPatient.medical_history?.map((condition, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <Typography variant='subtitle2' gutterBottom>
                  {condition.name}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Diagnosed:{' '}
                  {format(new Date(condition.diagnosed_date), 'MMM d, yyyy')}
                </Typography>
                {condition.notes && (
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    {condition.notes}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Stack spacing={2}>
            {selectedPatient.documents?.map((doc, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                    <Assignment />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant='subtitle2'>{doc.name}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Uploaded:{' '}
                      {format(new Date(doc.upload_date), 'MMM d, yyyy')}
                    </Typography>
                  </Box>
                  <Button size='small' variant='outlined'>
                    View
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Stack spacing={2}>
            {selectedPatient.notes?.map((note, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <Typography variant='subtitle2' gutterBottom>
                  {format(new Date(note.date), 'MMM d, yyyy h:mm a')}
                </Typography>
                <Typography variant='body2'>{note.content}</Typography>
              </Box>
            ))}
          </Stack>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant='contained'
          color='primary'
          startIcon={<ChatIcon />}
          onClick={() => navigate(`/doctor-dashboard/messaging/${patientId}`)}
        >
          Message
        </Button>
        <Button
          variant='contained'
          color='success'
          startIcon={<MedicalServicesIcon />}
          onClick={handleStartConsultation}
        >
          Start Consultation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDetailsModal;
